import { FC, useEffect } from 'react';

import { differenceInDays, isValid, parseISO } from 'date-fns';
import { Settings } from 'utils/constants/dynamicSettings';

import localStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { DashboardBanner } from 'components/Dashboard/DashboardBanner/DashboardBanner';

import { selectIsYandexVideoBannerVisible, setIsYandexVideoBannerVisible } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'dashboard.yandexVideoBanner.title',
    description: 'dashboard.yandexVideoBanner.description',
};

const YANDEX_VIDEO_BANNER_EXPIRATION_DATE = '2025-12-01';
const YANDEX_VIDEO_BANNER_LAST_DATE = 'YANDEX_VIDEO_BANNER_LAST_DATE';
const YANDEX_VIDEO_BANNER_SHOW_COUNT = 'YANDEX_VIDEO_BANNER_SHOW_COUNT';
const YANDEX_VIDEO_BANNER_SHOW_PERIOD_IN_DAYS = 3;
const YANDEX_VIDEO_BANNER_MAX_SHOWS = 4;

export const YandexVideoBanner: FC = () => {
    const isVisible = useSelector(selectIsYandexVideoBannerVisible);

    const trls = useTrls();
    const settings = useSettings();
    const dispatch = useDispatch();

    const isYandexVideoBannerEnabled = !!settings(Settings.EnableYandexVideoBanner);

    useEffect(() => {
        if (!isYandexVideoBannerEnabled) {
            dispatch(setIsYandexVideoBannerVisible(false));
            return;
        }

        const endDate = parseISO(YANDEX_VIDEO_BANNER_EXPIRATION_DATE);
        const currentDate = new Date();
        if (currentDate > endDate) {
            dispatch(setIsYandexVideoBannerVisible(false));
            return;
        }

        const showCountString = localStorageWrapper.getItem(YANDEX_VIDEO_BANNER_SHOW_COUNT) || '0';
        const showCount = parseInt(showCountString, 10);
        if (showCount >= YANDEX_VIDEO_BANNER_MAX_SHOWS) {
            dispatch(setIsYandexVideoBannerVisible(false));
            return;
        }

        const lastShownDateString = localStorageWrapper.getItem(YANDEX_VIDEO_BANNER_LAST_DATE) || '';
        let shouldShowByPeriod = false;
        const lastShownDate = parseISO(lastShownDateString);

        if (isValid(lastShownDate)) {
            const daysSinceLastShown = differenceInDays(new Date(), lastShownDate);
            shouldShowByPeriod = daysSinceLastShown >= YANDEX_VIDEO_BANNER_SHOW_PERIOD_IN_DAYS;
        } else {
            shouldShowByPeriod = true;
        }

        if (shouldShowByPeriod) {
            dispatch(setIsYandexVideoBannerVisible(true));
            localStorageWrapper.removeItem(YANDEX_VIDEO_BANNER_LAST_DATE);
        }
    }, [dispatch, isYandexVideoBannerEnabled]);

    const onClose = () => {
        localStorageWrapper.setItem(YANDEX_VIDEO_BANNER_LAST_DATE, new Date().toISOString());

        const showCountString = localStorageWrapper.getItem(YANDEX_VIDEO_BANNER_SHOW_COUNT) || '0';
        const showCount = parseInt(showCountString, 10);
        localStorageWrapper.setItem(YANDEX_VIDEO_BANNER_SHOW_COUNT, (showCount + 1).toString());

        dispatch(setIsYandexVideoBannerVisible(false));
    };

    if (!isYandexVideoBannerEnabled || !isVisible) {
        return null;
    }

    return <DashboardBanner title={trls(TrlKeys.title)} description={trls(TrlKeys.description)} onClose={onClose} />;
};
